import type { CreateAccountDetails, LoginCredentials, ResetPasswordDetails, SetPasswordDetails, SetPasswordResult } from '../domain/types'
import type { AutoSignInAPIRequest, CreateAccountAPIRequest, LoginAPIRequest, LoginErrorAPIResponse, ResetPasswordAPIRequest, SetPasswordAPIRequest, SetPasswordAPIResponse } from './types'
import defaultFetch from 'isomorphic-unfetch'
import { appendContextItemId } from '@/scopes/common/domain/policies/contextItemId'
import { createXsrfTokenClient } from '@/scopes/common/infrastructure/security/xsrfTokenClient'
import { LoginRequestError } from '../domain/LoginRequestError'
import { mapLoginError } from './loginErrorMapper'
import { mapSetPasswordResult } from './setPasswordMapper'

interface Dependencies {
  env: {
    NEXT_PUBLIC_API_BASE_URL: string
  }
  fetch: typeof defaultFetch
  contextItemId: string
  xsrfTokenClient: ReturnType<typeof createXsrfTokenClient>
}

type AuthAPIRequest = LoginAPIRequest | CreateAccountAPIRequest | ResetPasswordAPIRequest | SetPasswordAPIRequest | AutoSignInAPIRequest

async function readJsonBody<T>(response: Response): Promise<T | undefined> {
  try {
    return await response.json() as T
  }
  catch {
    return undefined
  }
}

export function createAuthApiClient(dependencies: Partial<Dependencies> = {}) {
  const {
    env = {
      NEXT_PUBLIC_API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL,
    },
    fetch = defaultFetch,
    contextItemId = '',
    xsrfTokenClient = createXsrfTokenClient({ env, fetch, contextItemId }),
  } = dependencies as Dependencies

  function postJson(path: string, payload: AuthAPIRequest, headers: Record<string, string> = {}): Promise<Response> {
    return fetch(appendContextItemId(`${env.NEXT_PUBLIC_API_BASE_URL}${path}`, contextItemId), {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        ...headers,
      },
      body: JSON.stringify(payload),
    })
  }

  async function postWithXsrfToken(path: string, payload: AuthAPIRequest): Promise<Response> {
    const xsrfToken = await xsrfTokenClient.getToken()

    return postJson(path, payload, { 'X-XSRF-TOKEN': xsrfToken })
  }

  async function login({ email, password, recaptchaToken }: LoginCredentials): Promise<void> {
    const payload: LoginAPIRequest = { username: email, password, recaptchaToken }

    const response = await postWithXsrfToken('/auth/login', payload)

    if (!response.ok) {
      const body = await readJsonBody<LoginErrorAPIResponse>(response)

      throw new LoginRequestError(mapLoginError(body))
    }
  }

  async function createAccount({ email, newPassword, newsletterSun, newsletterSki, recaptchaToken }: CreateAccountDetails): Promise<void> {
    const payload: CreateAccountAPIRequest = { username: email, password: newPassword, newsletterSun, newsletterSki, recaptchaToken }

    const response = await postWithXsrfToken('/auth/createaccount', payload)

    if (!response.ok)
      throw new Error(`Create account request failed with status ${response.status}`)
  }

  async function resetPassword({ email, recaptchaToken }: ResetPasswordDetails): Promise<void> {
    const payload: ResetPasswordAPIRequest = { username: email, recaptchaToken }

    const response = await postWithXsrfToken('/auth/passwordreset', payload)

    if (!response.ok)
      throw new Error(`Reset password request failed with status ${response.status}`)
  }

  async function setPassword(token: string, { password, recaptchaToken }: SetPasswordDetails): Promise<SetPasswordResult> {
    const payload: SetPasswordAPIRequest = { token, password, recaptchaToken }

    const response = await postWithXsrfToken('/auth/setpassword', payload)

    if (!response.ok)
      throw new Error(`Set password request failed with status ${response.status}`)

    const body = await readJsonBody<SetPasswordAPIResponse>(response)

    return mapSetPasswordResult(body)
  }

  async function validatePasswordResetToken(token: string): Promise<boolean> {
    const url = appendContextItemId(`${env.NEXT_PUBLIC_API_BASE_URL}/auth/validatepasswordresettoken?token=${encodeURIComponent(token)}`, contextItemId)
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    })

    if (response.status >= 500)
      throw new Error(`Validate password reset token request failed with status ${response.status}`)

    return response.ok
  }

  async function autoLogin(token: string): Promise<void> {
    const payload: AutoSignInAPIRequest = { asit: token }

    const response = await postJson('/auth/autosignin', payload)

    if (!response.ok)
      throw new Error(`Auto login request failed with status ${response.status}`)
  }

  return {
    login,
    createAccount,
    resetPassword,
    setPassword,
    validatePasswordResetToken,
    autoLogin,
  }
}
